import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"

/**
 * Settings → Customer events → Google server events (GA4 Measurement Protocol, Google Ads click
 * conversion upload) and Tracking health for every server destination. The GA4 API secret is never
 * sent back: only its last 4.
 */
export type GoogleEventsStatus = {
  ga4: { measurement_id: string | null; connected: boolean; readable: boolean; secret_last4: string | null; enabled: boolean; send_renewals: boolean; browser_purchase: boolean; active: boolean; last_test_at: string | null; last_test_ok: boolean | null }
  google_ads: { conversion_action_id: string | null; enabled: boolean; send_renewals: boolean; ads_connected: boolean; customer_id: string | null; active: boolean; last_test_at: string | null; last_test_ok: boolean | null }
  google_ads_tag: { id: string | null; purchase_label: string | null; enhanced: boolean }
}
export type HealthRow = {
  id: string
  label: string
  active: boolean
  events: string[]
  sent_24h: number
  failed_24h: number
  waiting: number
  skipped_24h: number
  last_sent_at: string | null
  last_sent_event: string | null
  last_failure: { at: string; error: string | null } | null
  /** Per event name, rows created in the window. */
  by_event: Record<HealthWindow, EventCount[]>
  /** Destinations that take Purchase: paid storefront orders placed vs Purchase events sent for them. */
  purchases: Record<HealthWindow, PurchaseCheck> | null
}
export type HealthWindow = "24h" | "7d"
export type EventCount = { event_name: string; sent: number; failed: number; skipped: number; waiting: number }
export type MissingReason = "not_queued" | "waiting" | "failed" | "skipped"
export type PurchaseCheck = {
  orders_placed: number
  purchases_sent: number
  purchase_orders: number
  renewal_purchases: number
  matched: number
  missing: number
  missing_by_reason: Record<MissingReason, number>
  missing_orders: { id: string; display_id: number | null; reason: MissingReason }[]
  duplicates: number
  duplicate_orders: { id: string; count: number }[]
}
type Response = { google: GoogleEventsStatus; health: HealthRow[]; can_manage: boolean }
export type GoogleEventsSave = {
  ga4?: { api_secret?: string; remove_secret?: true; enabled?: boolean; send_renewals?: boolean; browser_purchase?: boolean }
  google_ads?: { conversion_action_id?: string; enabled?: boolean; send_renewals?: boolean }
}
export type GoogleTestResult = { ok: boolean; message: string | null; response: unknown }

const KEY = ["settings", "google-events"]
const PATH = "/admin/terify/settings/google-events"

export const useGoogleEvents = () => useQuery({ queryKey: KEY, queryFn: () => sdk.client.fetch<Response>(PATH), refetchInterval: 60_000 })

export function useSaveGoogleEvents() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (body: GoogleEventsSave) => sdk.client.fetch<Response>(PATH, { method: "POST", body }), onSuccess: (r) => qc.setQueryData(KEY, r) })
}

export function useTestGoogleEvents() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (target: "ga4" | "google_ads") => sdk.client.fetch<{ test: GoogleTestResult }>(`${PATH}/send-test`, { method: "POST", body: { target } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
